import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import axiosInstance from 'helpers/api';
import AuthCardLayout from 'layouts/AuthCardLayout';

interface DemoFormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  industry_type: string;
  team_size: string;
  current_crm: string;
  message: string;
}

const RequestDemo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState<DemoFormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry_type: '',
    team_size: '',
    current_crm: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post('/demo-requests/public/demo-request', formData);
      
      if (response.data.success) {
        setSuccess(true);
        // Redirect to sign-up page after 2 seconds
        setTimeout(() => {
          navigate('/sign-up');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthCardLayout>
        <Card className="text-center">
          <Card.Body className="p-5">
            <div className="text-success mb-3">
              <i className="fas fa-check-circle fa-4x"></i>
            </div>
            <h3>Thank You!</h3>
            <p>We've received your demo request and will contact you within 24 hours.</p>
            <p className="text-muted">Redirecting you to sign up...</p>
            <Spinner animation="border" variant="primary" className="mt-3" />
          </Card.Body>
        </Card>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout>
      <div className="text-center mb-4">
        <h3>Request Your Personalized Demo</h3>
        <p className="text-muted">See how KairosIQ can transform your business</p>
      </div>

      <Card>
        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Smith"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@company.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company/Brokerage Name *</Form.Label>
              <Form.Control
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="ABC Realty"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(555) 123-4567"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Industry/Business Type *</Form.Label>
              <Form.Select
                name="industry_type"
                value={formData.industry_type}
                onChange={handleChange}
                required
              >
                <option value="">Select your primary focus</option>
                <option value="residential">Residential Real Estate</option>
                <option value="commercial">Commercial Real Estate</option>
                <option value="property_management">Property Management</option>
                <option value="investment">Real Estate Investment</option>
                <option value="team_brokerage">Real Estate Team/Brokerage</option>
                <option value="mortgage_lending">Mortgage Lending</option>
                <option value="commercial_lending">Commercial Lending</option>
                <option value="private_lending">Private Lending</option>
                <option value="hard_money_lending">Hard Money Lending</option>
                <option value="credit_union">Credit Union</option>
                <option value="bank_financial">Bank/Financial Institution</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Team Size *</Form.Label>
              <Form.Select
                name="team_size"
                value={formData.team_size}
                onChange={handleChange}
                required
              >
                <option value="">Select your team size</option>
                <option value="solo">Solo Professional (Just Me)</option>
                <option value="small_team">2-5 Team Members</option>
                <option value="medium_team">6-15 Team Members</option>
                <option value="large_team">16-50 Team Members</option>
                <option value="enterprise">50+ Team Members</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Current CRM/LOS System</Form.Label>
              <Form.Select
                name="current_crm"
                value={formData.current_crm}
                onChange={handleChange}
              >
                <option value="">Select your current CRM/LOS</option>
                <option value="none">No CRM/LOS Currently</option>
                <option value="chime">Chime</option>
                <option value="follow_up_boss">Follow Up Boss</option>
                <option value="kvcore">KvCORE</option>
                <option value="top_producer">Top Producer</option>
                <option value="liondesk">LionDesk</option>
                <option value="wise_agent">Wise Agent</option>
                <option value="market_leader">Market Leader</option>
                <option value="encompass">Encompass</option>
                <option value="calyx_point">Calyx Point</option>
                <option value="bytepro">BytePro</option>
                <option value="mortgage_coach">Mortgage Coach</option>
                <option value="lendingpad">LendingPad</option>
                <option value="optimal_blue">Optimal Blue</option>
                <option value="loanlogics">LoanLogics</option>
                <option value="salesforce_financial">Salesforce Financial Services</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Additional Information</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your current challenges, goals, or any specific questions you have about KairosIQ..."
              />
            </Form.Group>

            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={loading}
              size="lg"
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Submitting...
                </>
              ) : (
                'Request Your Demo'
              )}
            </Button>
          </Form>

          <div className="text-center mt-3">
            <small className="text-muted">
              Already have an account? <a href="/sign-in">Sign in</a>
            </small>
          </div>
        </Card.Body>
      </Card>
    </AuthCardLayout>
  );
};

export default RequestDemo;